import React from "react";
import IndustryPage from "../components/IndustryPage";
import { Camera, PackageCheck, Thermometer, ShieldAlert } from "lucide-react";

export default function FoodDistribution({ onRequestDemo }) {
  return (
    <IndustryPage
      onRequestDemo={onRequestDemo}
      pill="FOOD DISTRIBUTION"
      title="FOOD"
      titleAccent="DISTRIBUTION"
      tagline="GRADE AT INTAKE. SHIP WITH CONFIDENCE."
      description="Verify the freshness and grade of every case, pallet, and produce lot the moment it hits your dock. StrainScan Pro catches spoilage, off-grade product, and mislabeled shipments before they ever leave the warehouse — protecting margin, contract terms, and downstream retail relationships."
      heroImage="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1600&q=85"
      heroLabel="INTAKE GRADED"
      heroScore="98.1%"
      sideStats={[
        { label: "GRADE", value: "US #1", sub: "APPROVED" },
        { label: "FRESHNESS", value: "94/100", sub: "PEAK" },
        { label: "LOT ID", value: "FD-2025-0912", sub: "PALLET 04" },
      ]}
      howSteps={[
        { icon: <Camera size={22} />, title: "CAPTURE AT DOCK", desc: "Overhead or handheld imaging as pallets are unloaded — one photo per case, or continuous conveyor feed." },
        { icon: <PackageCheck size={22} />, title: "GRADE INSTANTLY", desc: "USDA / EU / custom grade classification with per-piece defect scoring in under 800 ms." },
        { icon: <Thermometer size={22} />, title: "FRESHNESS SCORE", desc: "Cross-references visual freshness markers with cold-chain data to project remaining shelf life." },
        { icon: <ShieldAlert size={22} />, title: "REJECT & ALERT", desc: "Auto-flags out-of-spec shipments, generates supplier claim packets, and updates ERP in real time." },
      ]}
      capabilities={[
        { title: "USDA / EU GRADING", desc: "Automated grade classification aligned with USDA, EU, JAS, and 12+ regional grading standards out of the box." },
        { title: "SHELF-LIFE PROJECTION", desc: "Projects remaining shelf life from visual maturity + temperature history so you rotate FEFO with confidence." },
        { title: "DEFECT DETECTION", desc: "Identifies bruising, decay, sunscald, chill injury, dehydration, insect damage, and 25+ other defect classes." },
        { title: "SUPPLIER SCORECARD", desc: "Tracks per-vendor arrival quality across every SKU so procurement negotiates from real data, not gut feel." },
        { title: "CLAIM AUTOMATION", desc: "Generates photographic and structured-data claim packets the moment a shipment is rejected — instant supplier chargeback." },
        { title: "COLD-CHAIN OVERLAY", desc: "Correlates visual quality with cold-chain telemetry to pinpoint exactly where in transit product degraded." },
      ]}
      metrics={[
        { label: "GRADE ACCURACY", value: "99.2%", sub: "VS. USDA INSPECTOR" },
        { label: "INTAKE SPEED", value: "800ms", sub: "PER CASE" },
        { label: "SHRINK REDUCTION", value: "31%", sub: "TARGET" },
        { label: "SUPPLIERS TRACKED", value: "∞", sub: "PER-VENDOR SCORECARD" },
      ]}
      useCases={[
        { kpi: "31%", title: "LESS SHRINK", sub: "TARGET", desc: "Intake grading is designed to reduce write-offs across produce categories by up to 31%." },
        { kpi: "$4.20", title: "SAVED PER CASE", sub: "TARGET", desc: "Cost-avoidance target per case through earlier rejection, faster claims, and better shelf-life-driven allocation." },
        { kpi: "6→1", title: "STAFFING RATIO", desc: "Enables one supervisor to review exceptions instead of a six-inspector manual QC line — 24/7 coverage, zero fatigue." },
      ]}
      quote={{
        text: "Freshness shouldn't be a gamble — it should be a data point. Our vision is that every case, every pallet, every produce lot is graded and time-stamped before it leaves the dock. Trust in the supply chain begins with proof, not paperwork.",
        author: "Marshall Perkins",
        role: "Founder & CEO, StrainScan Pro",
      }}
    />
  );
}
